import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';

import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { ParentSummary } from 'us-react-components/dist/ParentSummary/ParentSummary';
import { RecurringScheduleDisplay } from 'us-react-components/dist/RecurringScheduleDisplay/RecurringScheduleDisplay';
import {
  getLongWeekdayDateString,
  timeSince,
} from 'us-react-components/dist/utils/date';
import { Job, JobService, JobType } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { getJobHeading, parseApiDate } from '../../utils/JobHeading';

import { CloseJobModal, CloseJobStatus } from './components/CloseJobModal';

const getHoursBetween = (startTime: string, endTime: string): number => {
  const stub = '2012-12-12T';
  const s = new Date(`${stub}${startTime}`);
  const e = new Date(`${stub}${endTime}`);
  return Math.abs(e.getTime() - s.getTime()) / 36e5;
};

// Mirrors getDurationString (hours → "X hours" / "Xhr Ymin") used by
// JobDisplayService.getJobDuration.
const getHoursLabel = (hours: number): string => {
  if (hours <= 0) return '';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  const whole = Math.floor(hours);
  const mins = Math.round((hours - whole) * 60);
  if (mins === 0) return `${whole} hour${whole === 1 ? '' : 's'}`;
  return `${whole} hr ${mins} min`;
};

const getJobDuration = (job: Job, t: (k: string, v?: Record<string, unknown>) => string): string => {
  if (job.type === JobType.ONE_TIME) {
    const s = parseApiDate(job.booking?.start);
    const e = parseApiDate(job.booking?.end);
    if (!s || !e) return '';
    return t('pages.jobDetail.durationFor', {
      duration: getHoursLabel((e.getTime() - s.getTime()) / 36e5),
    });
  }
  if (
    job.type === JobType.RECURRING &&
    job.recurring &&
    !job.recurring.flexible &&
    job.recurring.timeOfDay?.code === 'specific' &&
    job.recurring.days
  ) {
    const totalHours = job.recurring.days.reduce((sum, d) => {
      if (!d.startTime || !d.endTime) return sum;
      return sum + getHoursBetween(d.startTime, d.endTime);
    }, 0);
    return t('pages.jobDetail.durationPerWeek', { duration: getHoursLabel(totalHours) });
  }
  return '';
};

const getRateDisplay = (job: Job, t: (k: string) => string): string => {
  if (job.booking?.rate) return `$${job.booking.rate}/hr`;
  return t('pages.jobDetail.negotiableRate');
};

// Mirrors JobDisplayService.getPaymentLabel → "{type} payment" (lowercase,
// displayed text-capitalize in Angular).
const getPaymentLabel = (job: Job, t: (k: string, v?: Record<string, unknown>) => string): string =>
  t('pages.jobDetail.paymentLabel', { type: job.booking?.paymentType ?? 'unknown' });

const CLOSED_STATUSES = new Set([
  'closed',
  'closed_fulfilled_us',
  'closed_fulfilled_nus',
  'closed_not_fulfilled',
  'closed_invalid',
  'closed_by_parent',
  'closed_by_admin',
]);

const isClosedStatus = (status?: string): boolean =>
  !!status && (status.startsWith('closed') || CLOSED_STATUSES.has(status));

const getVerticalLabelKey = (typeId?: number): string | null => {
  switch (typeId) {
    case bookingVerticalType.CHILD_CARE:
      return 'pages.jobDetail.verticalChildCare';
    case bookingVerticalType.PET_CARE:
      return 'pages.jobDetail.verticalPetCare';
    case bookingVerticalType.SENIOR_CARE:
      return 'pages.jobDetail.verticalSeniorCare';
    case bookingVerticalType.HOUSEHOLD_SERVICES:
      return 'pages.jobDetail.verticalHouseholdServices';
    case bookingVerticalType.DAY_CARE:
      return 'pages.jobDetail.verticalDayCare';
    default:
      return null;
  }
};


export const JobDetail = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  type JobLoadState = 'loading' | 'loaded' | 'not-found' | 'forbidden' | 'error';
  const [job, setJob] = useState<Job | null>(null);
  const [loadState, setLoadState] = useState<JobLoadState>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(true);
  const isLoading = loadState === 'loading';

  useEffect(() => {
    document.title = 'UrbanSitter - Job Detail';
  }, []);

  useEffect(() => {
    const jobId = Number(pathId);
    if (!jobId || !user?.id) return;
    const controller = new AbortController();
    setLoadState('loading');
    const load = async (): Promise<void> => {
      try {
        const resp = await JobService.findById(jobId, { params: { userId: user.id }, signal: controller.signal });
        const data = (resp.data?.data ?? null) as Job | null;
        setJob(data);
        setLoadState('loaded');
        if (data?.status && isClosedStatus(data.status)) setDescriptionOpen(false);
      } catch (err) {
        const e = err as { code?: string; response?: { status?: number } };
        if (e?.code === 'ERR_CANCELED') return;
        const status = e?.response?.status;
        if (status === 404) setLoadState('not-found');
        else if (status === 403) setLoadState('forbidden');
        else setLoadState('error');
      }
    };
    void load();
    return (): void => controller.abort();
  }, [pathId, user?.id, loadAttempt]);

  const isParentOwner = !!(user?.parent && job?.booking?.parent?.id === user.id);
  const isClosed = isClosedStatus(job?.status);
  const applicantCount = job?.numberOfApplicants ?? 0;
  const typeId = job?.booking?.typeId;
  const verticalKey = getVerticalLabelKey(typeId);
  const postedAgo = useMemo(() => {
    if (!job?.created) return '';
    const d = typeof job.created === 'string' ? parseApiDate(job.created) : job.created;
    return d ? timeSince(d) : '';
  }, [job?.created]);

  const submitCloseJob = async (status: CloseJobStatus): Promise<void> => {
    if (!job?.id || !user?.id) return;
    await JobService.patch(
      job.id,
      { id: job.id, status } as Job,
      { params: { userId: user.id } },
    );
    toast.success(t('pages.jobDetail.closed_success'));
    setShowCloseModal(false);
    navigate('/posts');
  };

  if (isLoading) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('pages.jobDetail.loading')}</span>
      </div>
    );
  }

  if (loadState === 'not-found' || loadState === 'forbidden' || loadState === 'error') {
    return (
      <div className='container py-8'>
        <div className='us-card rounded p-5 max-w-2xl mx-auto'>
          <h2 className='text-xl font-semibold mb-2'>
            {t(loadState === 'error' ? 'common.errors.loadFailedTitle' : `common.errors.${loadState === 'not-found' ? 'notFound' : 'forbidden'}`)}
          </h2>
          <p className='mb-4'>
            {t(loadState === 'error' ? 'common.errors.loadFailedBody' : `common.errors.${loadState === 'not-found' ? 'notFoundBody' : 'forbiddenBody'}`)}
          </p>
          {loadState === 'error' && (
            <Button
              buttonType={ButtonType.primary}
              onClick={(): void => setLoadAttempt((n) => n + 1)}
            >
              {t('common.errors.retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!job) return <div className='container py-8' />;

  const booking = job.booking;
  const parent = booking?.parent;
  const includes = ((booking as { includes?: Array<{ id?: number; label?: string }> } | undefined)?.includes) ?? [];
  const sweeteners =
    ((booking as { sweeteners?: Array<{ id?: number; label?: string }> } | undefined)
      ?.sweeteners) ?? [];
  const kidsAges = (booking as { kidsAges?: string } | undefined)?.kidsAges;
  const numPeople = booking?.numberOfChildren ?? 0;
  const parentName = parent
    ? `${parent.firstName ?? ''}${parent.lastName ? ` ${parent.lastName.charAt(0)}` : ''}`.trim()
    : '';
  const rateDisplay = getRateDisplay(job, t);
  const durationDisplay = getJobDuration(job, t);
  const paymentDisplay = booking?.paymentType ? getPaymentLabel(job, t) : '';
  const descriptionId = 'job-description';
  const canShowBanner = isParentOwner && (!isClosed || applicantCount > 0);
  const lastAccessLabel = (parent as { userStats?: { lastAccessLabel?: string } } | undefined)?.userStats?.lastAccessLabel;

  const renderPeopleCount = (): JSX.Element | null => {
    if (typeId === bookingVerticalType.HOUSEHOLD_SERVICES) return null;
    if (!numPeople) return null;
    if (typeId === bookingVerticalType.CHILD_CARE) {
      const label = t(numPeople === 1 ? 'pages.jobDetail.childSingular' : 'pages.jobDetail.childPlural');
      return (
        <div className='text-base font-semibold my-1'>
          {kidsAges
            ? t('pages.jobDetail.childrenWithAges', { count: numPeople, label, ages: kidsAges })
            : t('pages.jobDetail.childrenNoAges', { count: numPeople, label })}
        </div>
      );
    }
    if (typeId === bookingVerticalType.PET_CARE) {
      const label = t(numPeople === 1 ? 'pages.jobDetail.petSingular' : 'pages.jobDetail.petPlural');
      return (
        <div className='text-base font-semibold my-1'>
          {t('pages.jobDetail.petsLabel', { count: numPeople, label })}
        </div>
      );
    }
    if (typeId === bookingVerticalType.SENIOR_CARE) {
      const label = t(numPeople === 1 ? 'pages.jobDetail.seniorSingular' : 'pages.jobDetail.seniorPlural');
      return (
        <div className='text-base font-semibold my-1'>
          {t('pages.jobDetail.seniorsLabel', { count: numPeople, label })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className='container py-8 max-w-4xl'>
      {canShowBanner && (
        <JobDetailBanner
          isClosed={isClosed}
          applicantCount={applicantCount}
          onViewApplicants={(): void => navigate(`/job/${job.id}/interest`)}
          onEdit={(): void => navigate(`/job/${job.id}/edit`)}
          onClose={(): void => setShowCloseModal(true)}
        />
      )}

      <div className='us-card rounded p-6'>
        {((): JSX.Element | null => {
          const start = parseApiDate(booking?.start);
          if (!start) return null;
          return (
            <div className='flex items-center gap-3 mb-4'>
              <div className='relative flex-shrink-0 w-12 h-14 flex flex-col items-center justify-end pb-1 rounded border border-sapphire bg-white'>
                <FontAwesomeIcon
                  icon={faCalendar}
                  className='absolute inset-0 w-full h-full text-sapphire opacity-10'
                />
                <span className='relative text-xl font-bold text-sapphire leading-none'>
                  {start.getDate()}
                </span>
                <span className='relative text-[10px] uppercase tracking-wide text-sapphire'>
                  {start.toLocaleString('en-US', { month: 'short' })}
                </span>
              </div>
              <div className='text-sm font-semibold'>
                {getLongWeekdayDateString(start)}
              </div>
            </div>
          );
        })()}
        <div className='flex flex-col md:flex-row md:items-start md:gap-6'>
          <div className='flex-1'>
            <div className='flex items-start gap-4 mb-3'>
              {parent?.pictureUrl && parent.id != null && (
                <a href={`/app/parent/${parent.id}`} className='flex-shrink-0'>
                  <img
                    src={parent.pictureUrl}
                    alt='Parent profile'
                    className='w-20 h-20 rounded-full object-cover'
                  />
                </a>
              )}
              <div className='flex-1'>
                {parentName && parent?.id != null && (
                  <a
                    href={`/app/parent/${parent.id}`}
                    className='text-base font-bold text-sapphire hover:underline'
                  >
                    {parentName}
                  </a>
                )}
                <h1 className='text-base font-bold my-1'>{getJobHeading(job, t)}</h1>
                <div className='text-base font-semibold'>
                  <span>{booking?.address?.city}</span>
                  {booking?.address?.neighborhood && <span> - {booking.address.neighborhood}</span>}
                </div>
                {renderPeopleCount()}
                {job.type === JobType.ONE_TIME && (
                  <div className='text-base font-semibold mb-1'>
                    {t('pages.jobDetail.oneTimeJob')}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className='md:text-right md:min-w-[180px]'>
            <div className='mb-1 font-semibold'>
              {rateDisplay}
              {durationDisplay && <span className='ml-1 font-normal'>{durationDisplay}</span>}
            </div>
            {paymentDisplay && (
              <div className='mb-1 capitalize'>{paymentDisplay}</div>
            )}
            {job.minimumHours ? (
              <div className='mb-2 text-sm'>
                {t('pages.jobDetail.guaranteedHours', { hours: job.minimumHours })}
              </div>
            ) : null}
            {job.type === JobType.RECURRING && (
              <RecurringScheduleDisplay
                days={job.recurring?.days}
                timeSummary={job.recurring?.timeSummary}
                flexible={job.recurring?.flexible}
                sameTimes={job.recurring?.sameTimes}
                timeOfDay={job.recurring?.timeOfDay}
              />
            )}
          </div>
        </div>

        {isClosed && (
          <div className='flex justify-end'>
            <Button
              buttonType={ButtonType.tertiary}
              aria-expanded={descriptionOpen}
              aria-controls={descriptionId}
              onClick={(): void => setDescriptionOpen((o) => !o)}
              className='px-3 py-2 text-xl font-bold cursor-pointer'
            >
              <FontAwesomeIcon icon={descriptionOpen ? faChevronUp : faChevronDown} />
            </Button>
          </div>
        )}

        {descriptionOpen && (
          <div id={descriptionId}>
            <div className='my-2 text-sm'>
              {lastAccessLabel && (
                <div>
                  <FontAwesomeIcon icon={faBolt} className='mr-1 text-sapphire' />
                  <span className='font-semibold lowercase'>
                    {t('pages.jobDetail.activeLabel', { label: lastAccessLabel })}
                  </span>
                </div>
              )}
              {postedAgo && (
                <h4 className='text-sm font-semibold mt-1 mb-1'>
                  {t('pages.jobDetail.posted', { time: postedAgo })}
                </h4>
              )}
              {verticalKey && (
                <h4 className='text-sm font-semibold'>
                  {t('pages.jobDetail.lookingFor')} {t(verticalKey)}
                </h4>
              )}
            </div>

            {((booking as { message?: string })?.message || booking?.description) && (
              <p className='mb-5 whitespace-pre-line'>
                {(booking as { message?: string })?.message || booking?.description}
              </p>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {includes.length > 0 && (
                <div className='mb-3'>
                  <h4 className='font-semibold mb-1'>{t('pages.jobDetail.jobIncludes')}</h4>
                  <ul className='list-disc list-inside space-y-1'>
                    {includes.map((inc) => (
                      <li key={inc.id}>{inc.label}</li>
                    ))}
                  </ul>
                </div>
              )}
              {sweeteners.length > 0 && (
                <div className='mb-3'>
                  <h4 className='font-semibold mb-1'>{t('pages.jobDetail.dealSweeteners')}</h4>
                  <ul className='list-disc list-inside space-y-1'>
                    {sweeteners.map((s) => (
                      <li key={s.id}>{s.label}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ParentSummary parent={parent} minimized={isClosed} />

      <CloseJobModal
        isOpen={showCloseModal}
        onClose={(): void => setShowCloseModal(false)}
        onSubmit={submitCloseJob}
      />
    </div>
  );
});

interface BannerProps {
  isClosed: boolean;
  applicantCount: number;
  onViewApplicants: () => void;
  onEdit: () => void;
  onClose: () => void;
}

const JobDetailBanner = ({
  isClosed,
  applicantCount,
  onViewApplicants,
  onEdit,
  onClose,
}: BannerProps): JSX.Element => {
  const { t } = useTranslation();
  const hasApplicants = applicantCount > 0;
  const applicantLabelKey =
    applicantCount === 1
      ? 'pages.jobDetail.banner.applicantCountSingular'
      : 'pages.jobDetail.banner.applicantCountPlural';

  // When sitters have applied, promote the "View applications" CTA to a
  // prominent amber card at the top of the page — the primary action for a
  // parent is to review applicants, not edit or close the job.
  if (hasApplicants) {
    return (
      <div className='mb-4'>
        <div className='rounded-lg border-l-4 border-amber-400 bg-amber-50 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm'>
          <div className='flex items-center gap-3'>
            <div className='w-11 h-11 rounded-full bg-amber-400 text-white flex items-center justify-center text-lg font-bold flex-shrink-0'>
              {applicantCount}
            </div>
            <div>
              <div className='font-semibold text-base'>
                {t(applicantLabelKey, { count: applicantCount })}
              </div>
              <div className='text-sm text-neutral-700'>
                {t('pages.jobDetail.banner.applicantSubtitle', {
                  defaultValue: 'Review applications and find the right match.',
                })}
              </div>
            </div>
          </div>
          <div className='flex flex-wrap gap-2 justify-end'>
            <Button buttonType={ButtonType.primary} onClick={onViewApplicants}>
              {t('pages.jobDetail.banner.viewApplicantsCta', {
                defaultValue: 'View applications',
              })}
            </Button>
            {!isClosed && (
              <>
                <Button buttonType={ButtonType.secondary} onClick={onEdit}>
                  {t('pages.jobDetail.banner.editJob')}
                </Button>
                <Button buttonType={ButtonType.secondary} onClick={onClose}>
                  {t('pages.jobDetail.banner.closeJob')}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className='mt-2'>
          <span
            className={`inline-block px-2 py-1 rounded text-xs font-bold ${isClosed ? 'bg-neutral-200 text-neutral-700' : 'bg-emerald-100 text-emerald-800'}`}
          >
            {isClosed ? t('pages.jobDetail.banner.closedBadge') : t('pages.jobDetail.banner.openBadge')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className='us-card rounded p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
      <div className='flex items-center gap-3'>
        <span
          className={`inline-block px-2 py-1 rounded text-xs font-bold ${isClosed ? 'bg-neutral-200 text-neutral-700' : 'bg-emerald-100 text-emerald-800'}`}
        >
          {isClosed ? t('pages.jobDetail.banner.closedBadge') : t('pages.jobDetail.banner.openBadge')}
        </span>
      </div>
      <div className='flex flex-wrap gap-2'>
        {!isClosed && (
          <>
            <Button buttonType={ButtonType.secondary} onClick={onEdit}>
              {t('pages.jobDetail.banner.editJob')}
            </Button>
            <Button buttonType={ButtonType.secondary} onClick={onClose}>
              {t('pages.jobDetail.banner.closeJob')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
